// @flow weak

import ElementPortal from 'react-element-portal';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import createReactClass from 'create-react-class'; // eslint-disable-line no-restricted-syntax
import dataAttributes from 'data-attributes';
import { RouterHistoryContainer, findRoutes } from 'redux-router-kit';

import { NotFound, StylesReset } from '@zapier/common-components';

import CatchErrorContainer from 'app/common/components/CatchErrorContainer';
import ChangeActiveAccountOnTabFocusContainer from 'app/common/components/ChangeActiveAccountOnTabFocusContainer';
import CheckPendingPolicies from 'app/common/components/CheckPendingPolicies';
import EnsureElement from 'app/common/components/EnsureElement';
import GlobalModalHolderContainer from 'app/common/components/GlobalModalHolderContainer';
import HeaderRenderer from 'app/viewport/components/HeaderRenderer';
import HelpButtonContainer from 'app/common/components/HelpButton';
import LoadingNextRouteIndicator from 'app/common/components/LoadingNextRouteIndicator';
import MaybeShowFocusOutline from 'app/common/components/MaybeShowFocusOutline';
import MaybeTrackLandingOnMountContainer from 'app/track/components/MaybeTrackLandingOnMountContainer';
import NPSSurvey from 'app/common/components/NPSSurvey';
import PageNotificationContainer from 'app/common/components/PageNotificationContainer';
import RefreshChrome from 'app/common/components/RefreshChrome';
import SignUpFormContainer from 'app/login/components/SignUpFormContainer';
import TeamsGuideAppContainer from 'app/team-guide/components/AppContainer';
import TopBar from 'app/viewport/components/TopBar';
import UrlBasedAccountLimitReloader from 'app/viewport/components/UrlBasedAccountLimitReloader';
import WebUniversalNavPageLayout from 'app/common/components/WebUniversalNavPageLayout';
import _str from 'app/common/underscore-string';
import routerActions from 'app/common/components/routerActions';

const ServerAppWrapper = ({ Wrapper, children, wrapperProps }) => {
  if (!Wrapper) {
    return children;
  }
  return <Wrapper {...wrapperProps}>{children}</Wrapper>;
};

const doesRequireServerApp = route => {
  return !!(
    route &&
    (_.isFunction(route.doesRequireServerApp)
      ? route.doesRequireServerApp()
      : route.doesRequireServerApp)
  );
};

const onTopBarMissing = () => {
  const topBarEl = document.createElement('div');
  topBarEl.className = 'topbar-holder';
  topBarEl.setAttribute('role', 'complementary');
  if (document.body) {
    document.body.insertBefore(topBarEl, document.body.firstChild);
  }
};

// We need to ensure that there is a header element because some pages might
// not send the header along with their Django template (which is fine because
// it means that the page should probably not contain a <Header />). But
// when the client navigates away from that page, we definitely need the Header
// to exist. So this will ensure at the least that a empty Header shell is
// created. <HeaderContainer /> will determine if that shell actually contains
// any content.
const onHeaderMissing = () => {
  const headerEl = document.createElement('div');
  headerEl.id = 'header';
  if (document.body) {
    document.body.insertBefore(headerEl, document.body.firstChild);
  }
};

// XXX: used internally by SignUp / TopBar, would need to get rid of that eventually
const mapDomNodeToProps = domNode => ({
  domNode,
  data: dataAttributes(domNode),
});

const Portals = ({ hasHeaderFromServer, serverHeaderType }) => (
  <React.Fragment>
    <EnsureElement onMissing={onHeaderMissing} selector="#header">
      <ElementPortal selector="#header">
        <HeaderRenderer
          hasHeaderFromServer={hasHeaderFromServer}
          serverHeaderType={serverHeaderType}
        />
      </ElementPortal>
    </EnsureElement>
    <ElementPortal id="support" shouldReset={true}>
      <React.Fragment>
        <HelpButtonContainer />
        <TeamsGuideAppContainer />
      </React.Fragment>
    </ElementPortal>
    <EnsureElement onMissing={onTopBarMissing} selector=".topbar-holder">
      <ElementPortal
        mapDomNodeToProps={mapDomNodeToProps}
        selector=".topbar-holder"
        view={TopBar}
      />
    </EnsureElement>
    <ElementPortal
      id="signup-form-wrapper"
      mapDomNodeToProps={mapDomNodeToProps}
      shouldReset={true}
      view={() => <SignUpFormContainer enableGoogleOneTap={true} />}
    />
  </React.Fragment>
);

const RefreshChromeIfRequired = ({
  matchedRoutes,
  children,
  shouldPassThrough,
  hasHeaderFromServer,
  serverHeaderType,
}) => {
  const shouldRefreshChrome =
    !shouldPassThrough &&
    !matchedRoutes.some(
      route => route.isFullPage || doesRequireServerApp(route)
    );
  return (
    <RefreshChrome shouldPassThrough={!shouldRefreshChrome}>
      <Portals
        hasHeaderFromServer={hasHeaderFromServer}
        serverHeaderType={serverHeaderType}
      />
      {children}
    </RefreshChrome>
  );
};

const Route = routerActions(
  createReactClass({
    displayName: 'Route',

    propTypes: {
      children: PropTypes.node,
      routes: PropTypes.object.isRequired,
      router: PropTypes.object.isRequired,
      routeTo: PropTypes.func.isRequired,
    },

    componentDidUpdate() {
      this.reloadIfDoesRequireServerApp();
    },

    reloadIfDoesRequireServerApp() {
      const { router, routes, routeTo } = this.props;
      if (router.current && router.next) {
        const nextMatchedRoutes = findRoutes(routes, router.next.routeKey);
        if (nextMatchedRoutes) {
          const hasServerRoute = nextMatchedRoutes.some(route =>
            doesRequireServerApp(route)
          );
          if (hasServerRoute) {
            routeTo(router.next.url, {
              exit: true,
            });
          }
        }
      }
    },

    render() {
      return (
        <React.Fragment>
          {this.props.children}
          <UrlBasedAccountLimitReloader />
        </React.Fragment>
      );
    },
  })
);

const Root = createReactClass({
  displayName: 'Root',
  initialHref: (null: ?string),

  UNSAFE_componentWillMount() {
    this.initialHref = window.location.href;
  },

  onUpdateServerAppContainer(appContainer, { matchedRoutes, ...props } = {}) {
    if (this.props.initialContainerElement && appContainer) {
      appContainer.appendChild(this.props.initialContainerElement);
    }

    if (matchedRoutes) {
      matchedRoutes.forEach(route => {
        if (route.onUpdate) {
          route.onUpdate(props);
        }
      });
    }
  },

  renderNotFound({ router }) {
    const url = router.current.url;

    // Let the server be in charge of other routes for now.
    const isClientNotFound = url === '/app' || _str.startsWith(url, '/app/');

    const notFound = isClientNotFound ? (
      <NotFound />
    ) : (
      <div
        className="server-app-container--wrapper"
        ref={this.onUpdateServerAppContainer}
      />
    );

    return (
      <Route router={router} routes={this.props.routes}>
        <WebUniversalNavPageLayout>
          <RefreshChromeIfRequired
            hasHeaderFromServer={this.props.hasHeaderFromServer}
            matchedRoutes={[]}
            serverHeaderType={this.props.serverHeaderType}
            shouldPassThrough={!isClientNotFound}
          >
            {notFound}
          </RefreshChromeIfRequired>
        </WebUniversalNavPageLayout>
      </Route>
    );
  },

  renderDefault(props) {
    const { router, matchedRoutes } = props;
    const { initialRouteProps } = this.props;

    const onUpdate = appContainer =>
      this.onUpdateServerAppContainer(appContainer, props);

    const routeWithServerWrapper = _.find(matchedRoutes, route => {
      return !!(route && route.ServerWrapper);
    });

    const { ServerWrapper } = routeWithServerWrapper || {};

    const div =
      this.initialHref === router.current.location.href ? (
        <RefreshChromeIfRequired
          hasHeaderFromServer={this.props.hasHeaderFromServer}
          matchedRoutes={matchedRoutes}
          serverHeaderType={this.props.serverHeaderType}
          shouldPassThrough={true}
        >
          <ServerAppWrapper
            Wrapper={ServerWrapper}
            wrapperProps={initialRouteProps}
          >
            <div ref={onUpdate} />
          </ServerAppWrapper>
        </RefreshChromeIfRequired>
      ) : (
        <RefreshChromeIfRequired
          hasHeaderFromServer={this.props.hasHeaderFromServer}
          matchedRoutes={matchedRoutes}
          serverHeaderType={this.props.serverHeaderType}
        >
          <ServerAppWrapper Wrapper={ServerWrapper}>
            <div className="app" id="app" ref={onUpdate} />
          </ServerAppWrapper>
        </RefreshChromeIfRequired>
      );

    return (
      <Route router={router} routes={this.props.routes}>
        <WebUniversalNavPageLayout>
          <MaybeTrackLandingOnMountContainer>
            {div}
          </MaybeTrackLandingOnMountContainer>
        </WebUniversalNavPageLayout>
      </Route>
    );
  },

  createElement(Component, props) {
    const { router } = props;
    const serverAppNode =
      this.initialHref === router.current.location.href
        ? this.props.initialAppDOM
        : undefined;
    return (
      <Component
        {...props}
        routes={this.props.routes}
        serverAppNode={serverAppNode}
      />
    );
  },

  renderRoot(props) {
    const { router, matchedRoutes, children } = props;

    if (!children) {
      return this.renderDefault(props);
    }

    const shouldRenderInAppDiv = !matchedRoutes.some(
      route => route.component && route.component.doesProvideAppElement
    );
    return (
      <CatchErrorContainer>
        <Route router={router} routes={this.props.routes}>
          {/* show a loading bar when loading an async route */}
          {router.fetch && <LoadingNextRouteIndicator />}

          <RefreshChromeIfRequired
            hasHeaderFromServer={this.props.hasHeaderFromServer}
            matchedRoutes={matchedRoutes}
            serverHeaderType={this.props.serverHeaderType}
          >
            <MaybeTrackLandingOnMountContainer>
              {shouldRenderInAppDiv ? (
                <div className="app" id="app">
                  <WebUniversalNavPageLayout>
                    {children}
                  </WebUniversalNavPageLayout>
                </div>
              ) : (
                children
              )}
            </MaybeTrackLandingOnMountContainer>
          </RefreshChromeIfRequired>
        </Route>
      </CatchErrorContainer>
    );
  },

  renderBeforeCurrent() {
    return (
      <React.Fragment>
        <div
          className="server-app-container--wrapper"
          ref={this.onUpdateServerAppContainer}
        />
      </React.Fragment>
    );
  },

  render() {
    const { routes } = this.props;

    return (
      <React.Fragment>
        <RouterHistoryContainer
          createElement={this.createElement}
          renderBeforeCurrent={this.renderBeforeCurrent}
          renderNotFound={this.renderNotFound}
          renderRoot={this.renderRoot}
          routes={routes}
          shouldEmitCrossOriginLinks={true}
        />
        <NPSSurvey />
        <ChangeActiveAccountOnTabFocusContainer />
        <GlobalModalHolderContainer />
        <PageNotificationContainer />
        <MaybeShowFocusOutline />
        <CheckPendingPolicies />
        <StylesReset />
      </React.Fragment>
    );
  },
});

export default Root;
